import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { generateJournalPrompts } from '../../services/gptService';
import GlitchText from '../../chaos/GlitchText';
import { useStorm } from '../../storm/StormContext';

const MIN_WORDS = 40;
const PAUSE_MS = 4000;
const SHOW_MS = 12000;
const COOLDOWN_MS = 30000;

// After a real pause on a long-enough entry, one follow-up question resolves out of the
// noise beside the textarea. Click to use it; ignore it and it dissolves. Failures are silent.
const FollowUp: React.FC<{ text: string; location: string; onUse: (q: string) => void }> = ({ text, location, onUse }) => {
  const { reducedMotion } = useStorm();
  const [q, setQ] = useState<string | null>(null);
  const lastAsk = useRef(0);
  const reqId = useRef(0);

  useEffect(() => {
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    reqId.current++; // any edit invalidates an in-flight answer
    if (words < MIN_WORDS || q) return;
    const myId = reqId.current;
    const t = window.setTimeout(async () => {
      if (Date.now() - lastAsk.current < COOLDOWN_MS) return;
      lastAsk.current = Date.now();
      try {
        const [first] = await generateJournalPrompts(text, location, MIN_WORDS, undefined, 1);
        if (myId === reqId.current && first && first.trim()) setQ(first.trim());
      } catch { /* silent by design */ }
    }, PAUSE_MS);
    return () => window.clearTimeout(t);
  }, [text, location, q]);

  useEffect(() => {
    if (!q) return;
    const t = window.setTimeout(() => setQ(null), SHOW_MS);
    return () => window.clearTimeout(t);
  }, [q]);

  return (
    <div className="gj-followup" aria-live="polite">
      <AnimatePresence>
        {q && (
          <motion.button key={q} className="gj-followup-q" onClick={() => { onUse(q); setQ(null); }}
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, x: 12 }} animate={reducedMotion ? { opacity: 1 } : { opacity: 1, x: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, filter: 'blur(8px)' }}
            transition={{ duration: 0.4 }}>
            <GlitchText resolve text={q} seedKey={q} style={{ fontSize: 18, lineHeight: 1.35 }} />
            <span className="gj-followup-hint">tap to write about this</span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FollowUp;
